/**
 * Consulting-move SSOT — what the mouth does this turn.
 * Coverage / report readiness can still track gaps. The reply must not become the form.
 */

import {
  ADAPTIVE_PACE_RULE,
  GROUNDED_PACK_ANSWER_RULE,
  HYPOTHESIS_LOOP_RULE,
  ICEBREAKER_RULE,
  NAMED_PROBLEM_REPLY_BIND,
  RESEARCH_TRICKLE_RULE,
} from '../../contracts.js'
import {
  formatHypothesisCorrectionBind,
  isHypothesisCorrection,
} from './working-hypothesis.js'
import {
  BRIEF_COVERAGE_SIDE_QUEST_RULE,
  isRemainingDiscoveryAsk,
} from '../../session/session-facts.js'

const RECAP_NEGATION_PATTERN =
  /\b(?:no|skip|without|don't|dont|do not|never)\s+(?:a\s+|the\s+)?recap\b|\b(?:no|skip|without)\s+extra questions\b|\b(?:no|don't|dont|do not)\s+(?:more|another|extra)\s+questions?\b|\bdon't ask\b|\bdo not ask\b/i

const WEEK_ONE_PLAN_PATTERN =
  /\bmonday of week one\b|\bweek one\b|\bfirst bottleneck\b|\bpick the (?:first|top) bottleneck\b|\bwhat monday\b|\bmonday (?:morning|of)\b/i

const GOAL_QUESTION_REPLAY_PATTERN =
  /what are you hoping to achieve|what's the main goal|what is the main goal|what specific business goals|to help get started/i

export function isRecapNegated(text: string | null | undefined): boolean {
  if (!text) return false
  return RECAP_NEGATION_PATTERN.test(visibleUserTextForIntent(text))
}

export function isWeekOnePlanAsk(text: string | null | undefined): boolean {
  if (!text) return false
  const visible = visibleUserTextForIntent(text)
  return isRecapNegated(visible) || WEEK_ONE_PLAN_PATTERN.test(visible)
}

export function isGoalQuestionReplay(text: string | null | undefined): boolean {
  if (!text) return false
  return GOAL_QUESTION_REPLAY_PATTERN.test(visibleUserTextForIntent(text))
}

const NAMED_PLACE_PATTERN =
  /\b(inbox|inboks|gmail|e-?mail|quotes?|tilbud|follow-?ups?|oppfølging)\b/i
const NAMED_DELAY_PATTERN =
  /\b(week|weeks|uke|uker|days?|dager|before anyone|sit(?:s|ting)?|ligger|rotting|unanswered|ubesvart|follows? up|følger opp)\b/i

/** Visitor already said the problem and where it sits. Not a category survey. */
export function isNamedOperationalProblem(text: string | null | undefined): boolean {
  const value = visibleUserTextForIntent(text).replace(/\s+/g, ' ').trim()
  if (!value || /NAMED PROBLEM/i.test(value)) return false
  return NAMED_PLACE_PATTERN.test(value) && NAMED_DELAY_PATTERN.test(value)
}

/** Live typed turn: visitor text, then the hidden mid-call bind when it applies. */
export function composeLiveTypedOutbound(trimmed: string): string {
  const parts = [trimmed]
  if (isNamedOperationalProblem(trimmed)) parts.push(NAMED_PROBLEM_REPLY_BIND)
  if (isHypothesisCorrection(trimmed)) parts.push(formatHypothesisCorrectionBind(trimmed))
  return parts.join('\n\n')
}

const ICEBREAKER_STEER_LANE =
  /^(?:ai implementation|ai-implementering|ai implementering|ki-implementering|ki implementering|workshop|something else|noe annet)\.?$/i

/** Visitor tapped a steer chip. That label is the lane, not a diagnosis. */
export function isIcebreakerSteer(text: string | null | undefined): boolean {
  const value = visibleUserTextForIntent(text).replace(/\s+/g, ' ').trim()
  return ICEBREAKER_STEER_LANE.test(value)
}

/** Lead-facing copy must not invent a category label they did not use. */
export function scrubUnnamedProblemLabel(reply: string, visitorText?: string | null): string {
  if (!reply) return reply
  if (/\b(bottleneck|flaskehals)\b/i.test(visitorText || '')) return reply
  const unlabeled = reply
    .replace(/\bbottlenecks\b/gi, 'problems')
    .replace(/\bbottleneck\b/gi, 'problem')
    .replace(/\bflaskehalsen\b/gi, 'problemet')
    .replace(/\bflaskehals\b/gi, 'problem')
  return scrubInventedOpeningGuess(unlabeled)
}

/** Killed opening. Drop a diagnosis the visitor did not give. */
export function scrubInventedOpeningGuess(reply: string): string {
  if (!/my first (?:bottleneck|problem) guess|i'd start here|jeg ville startet her|correct me if it's wrong|rett meg om det er feil/i.test(reply)) {
    return reply
  }
  const cleaned = reply
    .replace(/my first (?:bottleneck|problem) guess is[^.?!]*[.?!]?/gi, '')
    .replace(/i'd start here:[^.?!]*[.?!]?/gi, '')
    .replace(/jeg ville startet her:[^.?!]*[.?!]?/gi, '')
    .replace(/that(?:'s| is) a guess\s*[—-]\s*correct me if it's wrong\.?/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned || 'Where should that land in the work?'
}

/**
 * File-attach extraction is injected into the user message for the model.
 * Intent routing must ignore that scaffolding — a filename like
 * `fbc-sample-proposal.pdf` is not a request for a delivery proposal.
 */
export function visibleUserTextForIntent(text: string | null | undefined): string {
  if (!text) return ''
  return text
    .replace(/\[PDF CONTENT:[\s\S]*$/gi, '')
    .replace(/\[IMAGE ANALYSIS:[\s\S]*$/gi, '')
    .replace(/\[File attached:[^\]]*\]/gi, '')
    .replace(/\[PDF:[^\]]*\]/gi, '')
    .trim()
}

export function lastUserTextFromMessages(
  messages: Array<{ role?: string; content?: unknown }> | undefined
): string {
  if (!Array.isArray(messages)) return ''
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role !== 'user') continue
    return typeof message.content === 'string' ? message.content : ''
  }
  return ''
}

/**
 * After the bottleneck is known, the mouth closes: generate the visitor
 * discovery brief (PDF), then Book a call. Coverage can still track.
 */
export function shouldCloseConsultingFunnel(params: {
  lastUserMessage?: string | null
  corePainCaptured?: boolean
  conversationObjective?: string | null
}): boolean {
  const planAsk = isWeekOnePlanAsk(params.lastUserMessage)
  const recapOff = isRecapNegated(params.lastUserMessage)
  const painKnown = params.corePainCaptured === true
  const recommending =
    params.conversationObjective === 'recommend' || params.conversationObjective === 'prove-value'
  return planAsk || recapOff || painKnown || recommending
}

/**
 * Private operating instruction for Discovery + Live.
 * Use facts. Do not read them back. Do not interview the next coverage field.
 */
export function getConsultingMoveInstruction(params: {
  lastUserMessage?: string | null
  corePainCaptured?: boolean
  conversationObjective?: string | null
  recommendedNext?: string | null
}): string {
  if (isRemainingDiscoveryAsk(params.lastUserMessage)) {
    return `═╝ CONSULTING MOVE THIS TURN (NON-NEGOTIABLE):
- They asked what is still open on the discovery brief. Answer from BRIEF COVERAGE only — the same labels as the Discovery Brief card.
- Say the N/9 count. If any field is missing, name every missing label and say those fields are optional — they can generate now. Never say you have no remaining discovery questions.
- If nothing is missing, say coverage is complete and they can generate.
- Do not pivot away to a visual observation, a new survey, or "I don't have any remaining discovery questions".
- ${BRIEF_COVERAGE_SIDE_QUEST_RULE}`
  }

  const namedProblem = isNamedOperationalProblem(params.lastUserMessage)
  const steerLane = isIcebreakerSteer(params.lastUserMessage)
  if (shouldCloseConsultingFunnel(params) || namedProblem) {
    return `═╝ CONSULTING MOVE THIS TURN (NON-NEGOTIABLE):
${namedProblem ? `${NAMED_PROBLEM_REPLY_BIND}\n` : ''}- You are in the room with them. Warm, direct, short. No catalog of capabilities. Never "Great question", "To help get started", "That's definitely a crucial gap", or "would be a top priority".
- Do NOT dump session context, Public/session context, intake fields, or a transcript replay (Assistant:/You:). Use the facts. Do not read them back.
- Do NOT ask what they hope to achieve, what the main goal is, or the next missing coverage field (volume, cost, business effect, budget). Report readiness can track coverage privately — it must not trap this conversation in UNDERSTANDING.
- They already named the problem and the place if those appear in the last user message. ONE Monday action there — what Monday of week one looks like. If they said the inbox, the action is in that inbox. If they said Gmail, the action is in Gmail — not a new CRM and not a tool survey. Do not ask whether they use a CRM or any tool they did not name.
- ${ADAPTIVE_PACE_RULE}
- CLOSE THE FUNNEL. The product PDF is the discovery brief the VISITOR GENERATES at the end of this conversation (PDF) via Generate / Export / Report, or generate_discovery_report. Then Book a call. That brief is not a file they upload. Visitor file attach is only for THEIR own docs — do not design this turn around uploading a proposal.
- A question is optional. If they said no recap / no extra questions / give the plan, do not ask. End with the first action, then tell them to Generate with current information and Book a call.
- Coverage gaps stay off-camera. Consult toward a week-one plan, the generated brief, and a call — not a 9-point form.`
  }

  const coverageNote = params.recommendedNext
    ? ` Coverage may still track "${params.recommendedNext}" privately — do not interview that field.`
    : ''
  return `═╝ CONSULTING MOVE THIS TURN:
- Sound like Farzad in the room: warm, direct, short. No filler, no "Great question", no capability catalog.
${steerLane ? '- They picked a steer lane (AI implementation, workshop, or something else). Answer that lane. Do not diagnose a personal problem. Do not ask them to correct a guess.\n' : ''}
- Do not recap session context or replay the transcript.
- ${HYPOTHESIS_LOOP_RULE}
- ${RESEARCH_TRICKLE_RULE}
- ${ADAPTIVE_PACE_RULE}
- If GROUNDED PACK is in context, ${ICEBREAKER_RULE} ${GROUNDED_PACK_ANSWER_RULE} That opening overrides an inferred personal problem.
- If a WORKING HYPOTHESIS is in CONTEXT or the Live seed, test or revise THAT one. Do not ask a new category checklist.${coverageNote}
- Prove you heard the last thing they said in one concrete clause. If they are still explaining, do not close. If one missing fact would change the next step, ask that. If the next step is already clear, offer it — not both.
- If they named Gmail, HubSpot, a CRM, or "just inbox/forms", the next action lives in that stack.`
}
